/*
 * This file is part of the https://gitlab-intern.easd-support.de/easd/p26s26_energie_watchdog project.
 *
 * (c) 2026 EA Systems Dresden GmbH
 *
 * For the full copyright and license information, please view the LICENSE.md
 * file that was distributed with this source code.
 */

import { Canvg } from 'canvg';
import mermaid from 'mermaid';
import { Check, Copy, FileCode2, ImageDown } from 'lucide-react';
import { useEffect, useId, useState } from 'react';

/* Import feedback components */
import StatusModal from '@/components/feedback/StatusModal';

/* Import context functions */
import { useI18n } from '@/context/I18nContext';

/* Import library functions */
import {
    createTimestampedFilename,
    downloadBlob,
    downloadTextFile,
} from '@/lib/presentation/fileDownload';

interface MermaidFigureProps {
    definition: string;
    fillParent?: boolean;
    theme?: 'light' | 'dark';
}

function normalizeMermaidSvg(svgContent: string): string {
    // Mermaid HTML labels can contain HTML-style <br> elements, while the
    // SVG parser used for the PNG export requires valid XML.
    return svgContent
        .replace(/<br\s*>/gi, '<br/>')
        .replaceAll('!important', '');
}

function getStyleValue(element: Element, property: string): string | null {
    const style = element.getAttribute('style');
    const match = style?.match(new RegExp(`${property}\\s*:\\s*([^;]+)`, 'i'));

    return match?.[1].trim() ?? null;
}

function getInheritedLabelColor(element: Element, theme: 'light' | 'dark'): string {
    let currentElement: Element | null = element;

    while (currentElement) {
        const color = getStyleValue(currentElement, 'color')
            ?? currentElement.getAttribute('color');

        if (color) {
            return color;
        }

        currentElement = currentElement.parentElement;
    }

    return theme === 'dark' ? '#f5f5f5' : '#1f2937';
}

function applyCanvasStyleAttributes(document: Document): void {
    document.querySelectorAll('[style]').forEach((element) => {
        [
            ['fill', 'fill'],
            ['stroke', 'stroke'],
            ['stroke-width', 'stroke-width'],
            ['font-family', 'font-family'],
            ['font-size', 'font-size'],
        ].forEach(([property, attribute]) => {
            const value = getStyleValue(element, property);

            if (value) {
                element.setAttribute(attribute, value);
            }
        });
    });
}

function getLabelLines(element: Element): string[] {
    const lines = [''];

    function appendNode(node: Node): void {
        if (node.nodeType === Node.TEXT_NODE) {
            lines[lines.length - 1] += node.textContent ?? '';
            return;
        }

        if (!(node instanceof Element)) {
            return;
        }

        if (node.localName === 'br') {
            lines.push('');
            return;
        }

        node.childNodes.forEach(appendNode);
    }

    element.childNodes.forEach(appendNode);

    return lines.map((line) => line.replace(/\s+/g, ' ').trim()).filter(Boolean);
}

function initializeMermaid(theme: 'light' | 'dark', exportMode = false): void {
    mermaid.initialize({
        startOnLoad: false,
        theme: theme === 'dark' ? 'dark' : 'default',
        flowchart: exportMode ? { htmlLabels: false } : undefined,
    });
}

function getSvgDimensions(svgContent: string): { height: number; width: number } {
    const svgElement = new DOMParser()
        .parseFromString(normalizeMermaidSvg(svgContent), 'image/svg+xml')
        .documentElement;
    const viewBoxValues = svgElement.getAttribute('viewBox')?.split(/\s+/).map(Number);

    if (viewBoxValues && viewBoxValues.length === 4) {
        return { height: viewBoxValues[3], width: viewBoxValues[2] };
    }

    return {
        height: Number(svgElement.getAttribute('height')) || 720,
        width: Number(svgElement.getAttribute('width')) || 1280,
    };
}

function createCanvasSafeSvg(svgContent: string, theme: 'light' | 'dark'): string {
    const document = new DOMParser().parseFromString(normalizeMermaidSvg(svgContent), 'image/svg+xml');
    const svgElement = document.documentElement;
    const namespace = 'http://www.w3.org/2000/svg';

    applyCanvasStyleAttributes(document);

    svgElement.querySelectorAll('foreignObject').forEach((foreignObject) => {
        const labelLines = getLabelLines(foreignObject);

        if (labelLines.length === 0) {
            foreignObject.remove();
            return;
        }

        const text = document.createElementNS(namespace, 'text');
        const width = Number(foreignObject.getAttribute('width')) || 0;
        const height = Number(foreignObject.getAttribute('height')) || 0;
        const x = Number(foreignObject.getAttribute('x')) || 0;
        const y = Number(foreignObject.getAttribute('y')) || 0;
        const lineHeight = 15;
        const firstLineY = y + (height / 2) - (((labelLines.length - 1) * lineHeight) / 2);

        text.setAttribute('fill', getInheritedLabelColor(foreignObject, theme));
        text.setAttribute('font-family', 'Arial, sans-serif');
        text.setAttribute('font-size', '14');
        text.setAttribute('text-anchor', 'middle');
        text.setAttribute('x', String(x + (width / 2)));
        text.setAttribute('y', String(firstLineY));
        labelLines.forEach((line, index) => {
            const lineElement = document.createElementNS(namespace, 'tspan');

            lineElement.setAttribute('x', String(x + (width / 2)));
            lineElement.setAttribute('dy', String(index === 0 ? 0 : lineHeight));
            lineElement.textContent = line;
            text.append(lineElement);
        });
        foreignObject.replaceWith(text);
    });

    return new XMLSerializer().serializeToString(svgElement);
}

async function createDiagramPng(svgContent: string, theme: 'light' | 'dark'): Promise<Blob> {
    const { height, width } = getSvgDimensions(svgContent);
    const canvas = document.createElement('canvas');
    canvas.height = Math.ceil(height);
    canvas.width = Math.ceil(width);
    const context = canvas.getContext('2d');

    if (!context) {
        throw new Error('Unable to create PNG export context.');
    }

    context.fillStyle = theme === 'dark' ? '#1d232a' : '#ffffff';
    context.fillRect(0, 0, canvas.width, canvas.height);

    const renderer = Canvg.fromString(context, createCanvasSafeSvg(svgContent, theme), {
        ignoreAnimation: true,
        ignoreClear: true,
        ignoreMouse: true,
    });

    await renderer.render({ ignoreDimensions: true });

    return new Promise<Blob>((resolve, reject) => {
        canvas.toBlob((blob) => {
            if (blob) {
                resolve(blob);
                return;
            }

            reject(new Error('Unable to create Mermaid diagram PNG.'));
        }, 'image/png');
    });
}

async function renderExportSvg(
    definition: string,
    diagramId: string,
    theme: 'light' | 'dark',
): Promise<string> {
    try {
        // foreignObject labels taint canvas exports in browsers. The visible diagram
        // keeps its HTML labels; the PNG-only rendering uses native SVG text labels.
        initializeMermaid(theme, true);
        const { svg } = await mermaid.render(
            `mermaid-export-${diagramId.replace(/:/g, '-')}-${Date.now()}`,
            definition,
        );

        return svg;
    } finally {
        initializeMermaid(theme);
    }
}

function MermaidFigure({
    definition,
    fillParent = false,
    theme = 'light',
}: MermaidFigureProps) {
    const { t } = useI18n();
    const diagramId = useId();
    const [errorMessage, setErrorMessage] = useState('');
    const [svgContent, setSvgContent] = useState('');
    const [copyState, setCopyState] = useState<'idle' | 'copied'>('idle');
    const [downloadErrorMessage, setDownloadErrorMessage] = useState('');

    useEffect(() => {
        if (copyState !== 'copied') {
            return;
        }

        const timeoutId = window.setTimeout(() => {
            setCopyState('idle');
        }, 1500);

        return () => {
            window.clearTimeout(timeoutId);
        };
    }, [copyState]);

    useEffect(() => {
        let isActive = true;

        async function renderDiagram(): Promise<void> {
            setErrorMessage('');

            try {
                initializeMermaid(theme);

                const { svg } = await mermaid.render(
                    `mermaid-${diagramId.replace(/:/g, '-')}`,
                    definition,
                );

                if (!isActive) {
                    return;
                }

                setSvgContent(svg);
            } catch (error) {
                if (!isActive) {
                    return;
                }

                setSvgContent('');
                setErrorMessage(
                    error instanceof Error
                        ? error.message
                        : 'Mermaid diagram rendering failed.',
                );
            }
        }

        void renderDiagram();

        return () => {
            isActive = false;
        };
    }, [definition, diagramId, theme]);

    async function copyDefinition(): Promise<void> {
        await navigator.clipboard.writeText(definition);
        setCopyState('copied');
    }

    function downloadDefinition(): void {
        downloadTextFile(
            definition,
            createTimestampedFilename('mermaid-diagram', 'mmd'),
            'text/vnd.mermaid',
        );
    }

    async function downloadDiagram(): Promise<void> {
        try {
            const exportSvgContent = await renderExportSvg(definition, diagramId, theme);
            const imageBlob = await createDiagramPng(exportSvgContent, theme);
            downloadBlob(imageBlob, createTimestampedFilename('mermaid-diagram', 'png'));
        } catch {
            setDownloadErrorMessage(t('Unable to download diagram.'));
        }
    }

    if (errorMessage) {
        return (
            <div className="time-series-api-plot">
                <div className="time-series-api-plot__message">{errorMessage}</div>
            </div>
        );
    }

    if (!svgContent) {
        return (
            <div className="time-series-api-plot">
                <div className="time-series-api-plot__message">Rendering Mermaid diagram...</div>
            </div>
        );
    }

    return (
        <div className={`mermaid-surface content-surface relative${fillParent ? ' mermaid-surface--fill' : ''}`}>
            <div className="action-controls absolute right-3 top-3 z-10 flex gap-1 rounded-box p-1">
                <div className="tooltip tooltip-left" data-tip={t(copyState === 'copied' ? 'Copied' : 'Copy Mermaid source')}>
                    <button
                        type="button"
                        className="btn btn-ghost btn-sm btn-square"
                        aria-label={t(copyState === 'copied' ? 'Copied' : 'Copy Mermaid source')}
                        onClick={() => {
                            void copyDefinition();
                        }}
                    >
                        {copyState === 'copied'
                            ? <Check className="h-4 w-4" aria-hidden="true" />
                            : <Copy className="h-4 w-4" aria-hidden="true" />}
                    </button>
                </div>
                <div className="tooltip tooltip-left" data-tip={t('Download Mermaid source')}>
                    <button
                        type="button"
                        className="btn btn-ghost btn-sm btn-square"
                        aria-label={t('Download Mermaid source')}
                        onClick={downloadDefinition}
                    >
                        <FileCode2 className="h-4 w-4" aria-hidden="true" />
                    </button>
                </div>
                <div className="tooltip tooltip-left" data-tip={t('Download diagram')}>
                    <button
                        type="button"
                        className="btn btn-ghost btn-sm btn-square"
                        aria-label={t('Download diagram')}
                        onClick={() => {
                            void downloadDiagram();
                        }}
                    >
                        <ImageDown className="h-4 w-4" aria-hidden="true" />
                    </button>
                </div>
            </div>
            <div
                className="mermaid-surface__content"
                dangerouslySetInnerHTML={{ __html: svgContent }}
            />
            <StatusModal
                isOpen={Boolean(downloadErrorMessage)}
                title={t('Download diagram')}
                message={downloadErrorMessage}
                onClose={() => {
                    setDownloadErrorMessage('');
                }}
            />
        </div>
    );
}

export default MermaidFigure;
